/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useCallback } from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { Box, ThemeProvider, createTheme } from '@mui/material';

import FullscreenSpinner from 'modules/common/fullscreenSpinner.component';
import ExpenseDetails from './expenseDetails.component';

import { COLOR_PRIMARY, COLOR_SECONDARY, COLOR_WARNING } from 'styles/theme';

import { getAuthorizationSummary } from 'modules/authorizations/store/actions/authorizations.actions';
import { getExpenseCodesByClientId } from 'modules/authorizations/store/actions/dataManagement.actions';
import { getClientDetails, setClientId } from 'modules/clients/clients.actions';

import {
  createExpense,
  updateExpense,
  deleteExpense,
  createExpenseLineItems,
  updateExpenseLineItems,
  deleteExpenseLineItem,
  getExpense,
  postBulkExpenseUpdateLineItems,
  resetExpense,
  setExpenseId,
  getExceptionsByAuthorizationId,
  pushExpenseToAcumatica
} from 'modules/expenses/store/expenses.actions';
import {
  getAllCountries,
  getAllCurrencies,
  getAllEnumerations,
  getAllProvinces,
  getAllSaleTypes,
  getAllWeightTypes
} from 'modules/enumerations/store/enumerationActions';
import { fetchEnumerations } from 'modules/authorizations/utils/fetchDataDetailsService';
import { showToast } from 'modules/layout/layout.actions';

export const ZERO_GUID = '00000000-0000-0000-0000-000000000000';
export const TRANSFEREE_PAYEE_TYPE = 'c3b2c3c1-0324-4c7b-8b97-ef3f3a4a3f6e';
export const EXPENSE_LINE_APPROVAL_STATUS = {
  DENIED: '79fe2e7c-6c1e-45d9-8d3b-600d4f82fbb3',
  PENDING: 'b15cf5f8-31bd-4a63-81d4-3c4f951a8686',
  PARTIAL_APPROVED: '6c1c39a0-f9d1-4a70-9d7e-b533b9d92966',
  APPROVED: '42f5a6d8-d62f-4f6a-9a6f-1a66f0e224d4'
};

export const EXPENSE_STATUS = {
  NOT_VIEWED: '6f3b939e-1f43-4a8b-a103-dbb3f1d3f4f9',
  ON_HOLD: '4ae21fb8-0b9b-4d9a-9b41-0f96e2680d9a',
  VIEWED: '4b9b6e2a-3ab0-44a9-8e54-8ea0db4a3518'
};

const buildExpenseDetailDto = ({ authorizationId, dateSubmitted = null, description = '', total = 0, expenseId = 0 } = {}) => ({
  payee: {
    payeeId: null,
    payeeTypeId: TRANSFEREE_PAYEE_TYPE,
    vendorId: null,
    authorizationId: authorizationId ?? null,
    transfereePayeeId: null,
    vendorPayeeId: null
  },
  expenseInformation: {
    expenseId: expenseId ?? 0,
    payeeId: 0,
    expenseStatusId: ZERO_GUID,
    dateSubmitted,
    description,
    total,
    expenseAnalystId: null,
    reimbursementTypeId: ZERO_GUID,
    payInCurrencyId: ZERO_GUID,
    equivalentCurrencyId: ZERO_GUID,
    batchTypeId: ZERO_GUID,
    isNotPayable: false,
    isRushPayment: false,
    isExpenseApproved: false
  },
  addressInformation: null,
  bankingInformation: null,
  expenseLineItems: []
});

const buildExpenseLineItemRow = ({
  expenseLineItemRow: {
    expenseId,
    lineItemId = null,
    description = '',
    totalAmount = 0,
    approvedAmount = 0,
    referenceString = '',
    glNumber = '',
    deniedReason = '',
    reportDate = null,
    payrollDate = null,
    dueDate = null,
    fromDate = null,
    toDate = null,
    entryDate = null,
    expenseDate = null,
    addedBy = 1,
    addedOn = new Date(),
    modifiedBy = 1,
    modifiedOn = new Date()
  },
  expenseLineItemExceptionRow: { exceptionCommentId = 0, isVoucherLineItem = false, isExpenseLineItem = true }
} = {}) => ({
  expenseLineItemRow: {
    lineItemId,
    expenseId,
    lineItemStatusId: ZERO_GUID,
    expenseCodeId: null,
    description,
    payOption: null,
    totalAmount,
    approvedAmount,
    referenceString,
    glNumber,
    deniedReason,
    reportDate,
    payrollDate,
    dueDate,
    fromDate,
    toDate,
    entryDate,
    expenseDate,
    addedBy,
    addedOn,
    modifiedBy,
    modifiedOn
  },
  expenseLineItemExceptionRow: { exceptionCommentId, isVoucherLineItem, isExpenseLineItem, lineItemId: 0 }
});

const ExpenseDetailsView = props => {
  const {
    expenseCodes,
    enumerations,
    match,
    getExpense,
    getAuthorizationSummary,
    getExpenseCodesByClientId,
    createExpense,
    updateExpense,
    deleteExpense,
    createExpenseLineItems,
    updateExpenseLineItems,
    deleteExpenseLineItem,
    postBulkUpdateLineItems,
    getExceptionsByAuthorizationId,
    pushExpenseToAcumatica
  } = props;

  const theme = createTheme({
    palette: { primary: COLOR_PRIMARY, secondary: COLOR_SECONDARY, warning: COLOR_WARNING }
  });

  const expenseIdParam = match?.params?.expenseId ?? '0';
  const expenseId = Number.parseInt(expenseIdParam, 10);

  const [isLoading, setIsLoading] = useState(false);
  const [expenseDetail, setExpenseDetail] = useState(null);
  const [authDetails, setAuthDetails] = useState(null);
  const [clientDetails, setClientDetails] = useState(null);
  const [authId, setAuthId] = useState(null);
  const [exceptions, setExceptions] = useState([]);

  const onCreateExpense = useCallback(
    async payload => {
      await createExpense(payload);
    },
    [createExpense]
  );

  const onUpdateExpense = useCallback(
    async (payload, resetStatus = false) => {
      const id = payload?.expenseInformation?.expenseId;
      if (!id) return;
      if (resetStatus) {
        await props.resetExpense(payload, id);
      } else {
        await updateExpense(payload, id);
      }
      const updated = (await getExpense(id)).response;
      setExpenseDetail(updated);
    },
    [updateExpense, getExpense]
  );

  const onDeleteExpense = useCallback(
    async id => {
      if (!id) return;
      await deleteExpense(id);
      if (props.history?.push) props.history.push('/expenses');
    },
    [deleteExpense, props.history]
  );

  const onSaveLineItem = useCallback(
    async item => {
      if (!expenseId) return;
      const hasId = !!item?.expenseLineItemRow?.lineItemId;
      if (hasId) {
        await updateExpenseLineItems(expenseId, item.expenseLineItemRow.lineItemId, item);
      } else {
        await createExpenseLineItems(
          expenseId,
          buildExpenseLineItemRow({ expenseLineItemRow: { expenseId }, expenseLineItemExceptionRow: {} })
        );
      }
      const refreshed = (await getExpense(expenseId)).response;
      setExpenseDetail(refreshed);
    },
    [expenseId, createExpenseLineItems, updateExpenseLineItems, getExpense]
  );

  const onDeleteLineItem = useCallback(
    async lineItemId => {
      if (!expenseId || !lineItemId) return;
      await deleteExpenseLineItem(expenseId, lineItemId);
      const refreshed = (await getExpense(expenseId)).response;
      setExpenseDetail(refreshed);
    },
    [expenseId, deleteExpenseLineItem, getExpense]
  );

  const onBulkUpdateStatus = useCallback(
    async lineItemStatusId => {
      if (!expenseId || !lineItemStatusId) return;
      await postBulkUpdateLineItems(expenseId, { targetStatusId: lineItemStatusId });
      const refreshed = (await getExpense(expenseId)).response;
      setExpenseDetail(refreshed);
    },
    [expenseId, postBulkUpdateLineItems, getExpense]
  );

  const onCopyLineItem = useCallback(
    async lineItemId => {
      if (!expenseId || !lineItemId) return;
      const itemToCopy =
        expenseDetail && Array.isArray(expenseDetail.expenseLineItems)
          ? expenseDetail?.expenseLineItems?.find(li => li.lineItemId === lineItemId)
          : null;

      const exceptionToCopy =
        expenseDetail && Array.isArray(expenseDetail.expenseLineItemExceptions)
          ? expenseDetail?.expenseLineItemExceptions?.find(e => e.lineItemId === lineItemId)
          : null;

      if (!itemToCopy) return;

      let newException = {};

      if (exceptionToCopy) {
        newException = {
          exceptionCommentId: exceptionToCopy.authorizationExceptionCommentId,
          lineItemId: 0,
          isExpenseLineItem: true,
          isVoucherLineItem: false
        };
      }

      const newItem = { ...itemToCopy, lineItemId: null, lineItemStatusId: ZERO_GUID };
      const request = { expenseLineItemRow: { ...newItem }, expenseLineItemExceptionRow: { ...newException } };
      await createExpenseLineItems(expenseId, request);
      const refreshed = (await getExpense(expenseId)).response;
      setExpenseDetail(refreshed);
    },
    [expenseId, createExpenseLineItems, expenseDetail, getExpense]
  );

  const pushExpenseToAcumaticaFunc = useCallback(async () => {
    if (!expenseId) return;
    await pushExpenseToAcumatica(expenseId);
    const refreshed = (await getExpense(expenseId)).response;
    setExpenseDetail(refreshed);
    return { response: refreshed };
  }, [expenseId, pushExpenseToAcumatica, getExpense]);

  const updateParentExpenseAttachments = newExpenseAttachments => {
    setExpenseDetail(prevDetail => ({
      ...prevDetail,
      expenseAttachments: newExpenseAttachments
    }));
  };

  const updateParentExpenseLineAttachments = newExpenseLineAttachments => {
    setExpenseDetail(prevDetail => ({
      ...prevDetail,
      expenseLineAttachments: newExpenseLineAttachments
    }));
  };

  useEffect(() => {
    const load = async () => {
      if (!Number.isFinite(expenseId) || expenseId <= 0) return;
      props.setExpenseId(expenseId);
      setIsLoading(true);
      try {
        const detail = (await getExpense(expenseId)).response;
        setExpenseDetail(detail);

        const derivedAuthId = detail?.payee?.authorizationId ?? null;
        let expenseCodeClientId = null;
        if (derivedAuthId) {
          const authDetails = (await getAuthorizationSummary(derivedAuthId)).response;
          const exceptions = (await getExceptionsByAuthorizationId(derivedAuthId)).response || [];
          setExceptions(exceptions);
          setAuthDetails(authDetails);
          setAuthId(derivedAuthId);

          const clientRecordId = authDetails?.authorizationDetails?.clientId ?? null;
          if (clientRecordId) {
            props.setClientId(clientRecordId);
            const clientResp = await props.getClientDetails(clientRecordId);
            setClientDetails(clientResp?.response ?? null);
            expenseCodeClientId = clientResp?.response?.clientDetails?.clientId || clientResp?.response?.clientId || null;
          }
        }

        await fetchEnumerations(props);

        if (expenseCodeClientId) {
          await getExpenseCodesByClientId(expenseCodeClientId);
        }
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <Box>
        {!isLoading && expenseDetail && enumerations !== undefined && (
          <ExpenseDetails
            authDetails={authDetails}
            expenseDetail={expenseDetail}
            expenseId={expenseId}
            authId={authId}
            clientRelocationPolicies={clientDetails?.clientRelocationPolicies ?? []}
            expenseCodes={expenseCodes}
            onCreateExpense={onCreateExpense}
            onUpdateExpense={onUpdateExpense}
            onDeleteExpense={onDeleteExpense}
            onSaveLineItem={onSaveLineItem}
            onDeleteLineItem={onDeleteLineItem}
            buildExpenseDetailDto={buildExpenseDetailDto}
            history={props.history}
            location={props.location}
            match={props.match}
            enumerations={enumerations.allEnums || []}
            onBulkUpdateStatus={onBulkUpdateStatus}
            onCopyLineItem={onCopyLineItem}
            updateParentExpenseAttachments={updateParentExpenseAttachments}
            updateParentExpenseLineAttachments={updateParentExpenseLineAttachments}
            pushExpenseToAcumaticaFunc={pushExpenseToAcumaticaFunc}
            exceptionSummaries={exceptions ? exceptions.exceptionSumamries : []}
            setExceptions={setExceptions}
          />
        )}
        {isLoading && <FullscreenSpinner />}
      </Box>
    </ThemeProvider>
  );
};

const mapStateToProps = state => {
  const { authorizationDetails, expenseCodes } = state.authorizations || {};
  const enumerations = state.enumerations;
  return {
    authorizationDetails,
    expenseCodes,
    enumerations
  };
};

export default compose(
  connect(mapStateToProps, {
    getExpense,
    getAuthorizationSummary,
    getAllEnumerations,
    getExpenseCodesByClientId,
    getClientDetails,
    setClientId,
    createExpense,
    updateExpense,
    deleteExpense,
    createExpenseLineItems,
    updateExpenseLineItems,
    deleteExpenseLineItem,
    getAllCountries,
    getAllCurrencies,
    getAllProvinces,
    getAllSaleTypes,
    getAllWeightTypes,
    showToast,
    postBulkUpdateLineItems: postBulkExpenseUpdateLineItems,
    resetExpense,
    setExpenseId,
    getExceptionsByAuthorizationId,
    pushExpenseToAcumatica
  })
)(ExpenseDetailsView);
